import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { initials } from '@/lib/format'
import { Button, cx } from './ui/primitives'

/**
 * Who you are signed in as, and the way out. Lives in the header rather than the
 * sidebar footer, because the sidebar is desktop-only and signing out should not
 * be a screen-size-dependent feature.
 */
export function UserMenu() {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!user) return null

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Signed in as ${user.displayName}`}
        onClick={() => setOpen((was) => !was)}
        className={cx(
          'flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-sunken',
          open && 'bg-sunken',
        )}
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-sunken font-mono text-[11px]">
          {initials(user.displayName)}
        </span>
        <span className="hidden max-w-40 truncate text-[13px] font-medium sm:block">
          {user.displayName}
        </span>
        <svg aria-hidden viewBox="0 0 12 12" className="size-3 text-muted">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-64 rounded-lg border border-line bg-surface p-1 shadow-float"
        >
          <div className="border-b border-line-soft px-3 py-2.5">
            <p className="truncate text-[13px] font-medium">{user.displayName}</p>
            <p className="truncate text-[12px] text-muted">{user.email}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              {user.roles.join(' · ') || 'No role'}
            </p>
          </div>

          <Button
            role="menuitem"
            variant="ghost"
            size="sm"
            className="mt-1 w-full justify-start"
            onClick={() => {
              setOpen(false)
              void signOut()
            }}
          >
            Sign out
          </Button>
        </div>
      )}
    </div>
  )
}
